import { Menu, ChevronLeft, Plus } from 'lucide-react';
import { useAppStore, type ViewMode } from '@/lib/store';
import { useState } from 'react';

interface MobileHeaderProps {
  onMenuClick: () => void;
  showBack?: boolean;
  onBack?: () => void;
  title?: string;
}

export function MobileHeader({ onMenuClick, showBack, onBack, title }: MobileHeaderProps) {
  const {
    viewMode,
    setViewMode,
    activeNovelId,
    novels,
  } = useAppStore();

  const [touchStartPos, setTouchStartPos] = useState<{ x: number, y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartPos) return;
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const diffX = touchStartPos.x - endX;
    const diffY = touchStartPos.y - endY;

    // Horizontal swipe threshold
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
      const views: ViewMode[] = ['dashboard', 'timeline', 'constellation', 'manuscript', 'stats', 'compost'];
      const currentIdx = views.indexOf(viewMode);
      if (diffX > 0 && currentIdx < views.length - 1) { // Swipe left -> next
        setViewMode(views[currentIdx + 1]);
      } else if (diffX < 0 && currentIdx > 0) { // Swipe right -> prev
        setViewMode(views[currentIdx - 1]);
      }
    }
    setTouchStartPos(null);
  };

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const activeChapter = activeNovel?.chapters.find(ch => ch.id === activeNovel?.activeChapterId);

  const getTitle = () => {
    if (title) return title;
    if (viewMode === 'dashboard') return 'My Novels';
    if (viewMode === 'manuscript' && activeChapter) return activeChapter.title;
    if (viewMode === 'constellation') return 'Constellation';
    if (viewMode === 'timeline') return 'Timeline';
    if (viewMode === 'compost') return 'Compost Heap';
    return 'Write Library';
  };

  const handleAdd = () => {
    if (viewMode === 'manuscript') {
      const { createChapter } = useAppStore.getState();
      createChapter();
    } else if (viewMode === 'dashboard') {
      // Trigger novel creation - this would need to be handled by Dashboard
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-4 z-30 md:hidden"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-light)'
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Left side */}
      <div className="flex items-center gap-3">
        {showBack ? (
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-lg active:scale-95 transition-transform min-w-12 min-h-12 flex items-center justify-center"
            style={{ color: 'var(--text-primary)' }}
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
          </button>
        ) : (
          <button
            onClick={onMenuClick}
            className="p-2 -ml-2 rounded-lg active:scale-95 transition-transform min-w-12 min-h-12 flex items-center justify-center"
            style={{ color: 'var(--text-primary)' }}
            aria-label="Open menu"
            aria-expanded={false}
            aria-controls="mobile-menu"
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Center - Title */}
      <h1
        className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[50%]"
        style={{ color: 'var(--text-primary)' }}
      >
        {getTitle()}
      </h1>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {viewMode === 'manuscript' && (
          <button
            onClick={handleAdd}
            className="p-2 rounded-lg active:scale-95 transition-transform min-w-12 min-h-12 flex items-center justify-center"
            style={{ color: 'var(--sage)' }}
            aria-label="Create new chapter"
          >
            <Plus className="w-5 h-5" aria-hidden="true" />
          </button>
        )}
      </div>
    </header>
  );
}
